// src/components/StokTakip.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, IconButton, TextField, InputAdornment, ToggleButtonGroup, ToggleButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import StockFormModal from '../components/modals/StockFormModal';

import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { exportToExcel } from '../utils/exportToExcel';
import useCachedCollection from '../hooks/useCachedCollection';

// Stok Excel kolon tanımı (salt-okunur export)
const stockExportColumns = [
    { header: 'Ürün Adı', value: (s) => s.urunAdi || '', width: 28 },
    { header: 'Stok Türü', value: (s) => s.stokTuru || '', width: 12 },
    { header: 'Marka', value: (s) => s.marka || '', width: 16 },
    { header: 'Alınan Firma', value: (s) => s.alinanFirma || '', width: 20 },
    { header: 'Alım Tarihi', value: (s) => s.alimTarihi, type: 'date', width: 14 },
    { header: 'Stok Adedi', value: (s) => s.stokAdedi, type: 'number', width: 11 },
    { header: 'Alış Fiyatı (₺)', value: (s) => s.alisFiyati, type: 'money', width: 15 },
    { header: 'Satış Fiyatı (₺)', value: (s) => s.satisFiyati, type: 'money', width: 15 },
];

const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR');
};

const StokTakip = () => {
    const [filteredStocks, setFilteredStocks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [stockToEdit, setStockToEdit] = useState(null);

    // Faz 7: realtime onSnapshot yerine oturum-içi cache (oturumda 1 kez okunur).
    const { data: stocksRaw, loading, refresh: refreshStocks } = useCachedCollection('stocks');
    // Eski Firestore orderBy("urunAdi","asc") sırasını koru (cache sırasız gelir).
    const stocks = useMemo(
        () => [...stocksRaw].sort((a, b) => (a.urunAdi || '').localeCompare(b.urunAdi || '', 'tr')),
        [stocksRaw]
    );

    useEffect(() => {
        const lowerTerm = searchTerm.toLowerCase();
        const filtered = stocks.filter(stock => {
            const matchesSearch =
                (stock.urunAdi || '').toLowerCase().includes(lowerTerm) ||
                (stock.marka || '').toLowerCase().includes(lowerTerm) ||
                (stock.alinanFirma || '').toLowerCase().includes(lowerTerm);

            if (filterType === 'all') return matchesSearch;
            return matchesSearch && stock.stokTuru === filterType;
        });
        setFilteredStocks(filtered);
    }, [searchTerm, stocks, filterType]);

    const handleAddClick = () => {
        setStockToEdit(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (stock) => {
        setStockToEdit(stock);
        setIsModalOpen(true);
    };

    const handleDeleteClick = async (id) => {
        if (window.confirm("Bu stok kalemini silmek istediğinize emin misiniz?")) {
            try {
                await deleteDoc(doc(db, "stocks", id));
                await refreshStocks(); // Faz 7: silinen kalem listeden hemen düşsün
            } catch (error) {
                console.error("Stok silinirken hata:", error);
                alert("Silme işlemi başarısız oldu.");
            }
        }
    };

    // Excel export (salt-okunur) — aktif filtre/aramaya uyan stok kalemlerini indirir
    const handleExportVisible = () => {
        if (filteredStocks.length === 0) {
            alert('Dışa aktarılacak stok kaydı bulunamadı.');
            return;
        }
        exportToExcel({ rows: filteredStocks, columns: stockExportColumns, sheetName: 'Stok', fileName: 'Stok_Listesi', scopeLabel: 'Goruntulenen' });
    };

    // Excel export (salt-okunur) — filtreden bağımsız tüm stok kalemlerini indirir
    const handleExportAll = () => {
        if (stocks.length === 0) {
            alert('Dışa aktarılacak stok kaydı bulunamadı.');
            return;
        }
        exportToExcel({ rows: stocks, columns: stockExportColumns, sheetName: 'Stok', fileName: 'Stok_Listesi', scopeLabel: 'Tumu' });
    };

    // Faz 7: ekranda yalnızca ilk `visibleCount` kalemi göster (DOM'u hafifletir).
    // Arama/filtre TÜM stok üzerinde çalışır; export da tüm filtreli veriyi indirir.
    const visibleStocks = filteredStocks.slice(0, visibleCount);

    return (
        <Box sx={{ p: { xs: 1, md: 1 } }}>
            <PageHeader
                title="Stok Takip"
                subtitle="Stok durumunu ve ürün takibini buradan yapabilirsiniz."
                icon={<Inventory2Icon />}
            >
                        <ToggleButtonGroup
                            value={filterType}
                            exclusive
                            onChange={(e, newFilter) => { if (newFilter) setFilterType(newFilter); }}
                            size="small"
                            sx={{ bgcolor: 'rgba(255,255,255,0.1)' }}
                        >
                            <ToggleButton value="all">Tümü</ToggleButton>
                            <ToggleButton value="Sarf">Sarf</ToggleButton>
                            <ToggleButton value="Cihaz">Cihaz</ToggleButton>
                            <ToggleButton value="Maske">Maske</ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                            size="small"
                            variant="outlined"
                            placeholder="Ürün, Marka veya Firma Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                        />
                        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshStocks} disabled={loading}>
                            Yenile
                        </Button>
                        <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />} onClick={handleExportVisible}>
                            Görüneni İndir
                        </Button>
                        <Button variant="outlined" color="success" startIcon={<FileDownloadIcon />} onClick={handleExportAll}>
                            Tümünü İndir
                        </Button>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick} sx={{ px: 3 }}>
                            Yeni Ürün Ekle
                        </Button>
            </PageHeader>

            <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0 }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Ürün Adı</TableCell>
                                <TableCell>Stok Türü</TableCell>
                                <TableCell>Marka</TableCell>
                                <TableCell>Alınan Firma</TableCell>
                                <TableCell>Alım Tarihi</TableCell>
                                <TableCell align="right">Stok Adedi</TableCell>
                                <TableCell align="right">Alış Fiyatı</TableCell>
                                <TableCell align="right">Satış Fiyatı</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleStocks.map((stock) => (
                                <TableRow key={stock.id} hover>
                                    <TableCell>{stock.urunAdi}</TableCell>
                                    <TableCell>{stock.stokTuru || '-'}</TableCell>
                                    <TableCell>{stock.marka || '-'}</TableCell>
                                    <TableCell>{stock.alinanFirma || '-'}</TableCell>
                                    <TableCell>{formatDate(stock.alimTarihi)}</TableCell>
                                    <TableCell align="right">{stock.stokAdedi}</TableCell>
                                    <TableCell align="right">{Number(stock.alisFiyati).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                    <TableCell align="right">{Number(stock.satisFiyati).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => handleEditClick(stock)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDeleteClick(stock.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredStocks.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ border: 0 }}>
                                        <EmptyState
                                            icon={<Inventory2Icon />}
                                            message="Stok kaydı bulunamadı."
                                            description={searchTerm || filterType !== 'all' ? 'Arama/filtre kriterlerinizle eşleşen ürün yok.' : 'Yeni bir ürün ekleyerek başlayın.'}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {visibleCount < filteredStocks.length && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
                        <Typography variant="caption" color="text.secondary">{visibleStocks.length} / {filteredStocks.length} gösteriliyor</Typography>
                        <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
                    </Box>
                )}
            </GlassCard>

            <StockFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                stockToEdit={stockToEdit}
                onSaved={refreshStocks}
            />
        </Box>
    );
};

export default StokTakip;
